import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'

type ListForProjectConfig = {
  getParams?: URLSearchParams
  withToken?: boolean
}

export async function listForProject(
  projectId: number | string,
  config: ListForProjectConfig,
): Promise<api.ApiResponse<api.Impact>> {
  const response = await api.api.get.projectImpact({
    urlParams: { projectId: String(projectId) },
    withToken: config.withToken,
    getParams: config.getParams,
  })

  return response.next((response) => response.body)
}

export async function updateProjectImpact(
  projectId: number | string,
  payload: api.UpdateImpact,
): Promise<api.ApiResponse<api.Impact>> {
  const response = await api.api.patch.projectImpact({
    urlParams: { projectId: String(projectId) },
    body: payload,
  })

  return response.next((response) => response.body)
}

type ImpactParams = {
  projectToken?: string
}

function toURLSearchParams(params: ImpactParams = {}, searchParams = new URLSearchParams()): URLSearchParams {
  if (params.projectToken) {
    searchParams.set('token', params.projectToken)
  }

  return searchParams
}

const IMPACT_CACHE = jotai.atom<hooks.Cache<api.Impact>>({})

type UseImpactConfig = {
  params?: ImpactParams
  fetchOnMount?: boolean | 'force'
  withToken?: boolean
}

export function useImpact(
  projectId?: number | string,
  { params = {}, withToken, fetchOnMount = true }: UseImpactConfig = {},
) {
  const sharedQuery = hooks.useSharedQuery(
    async (projectId: string | number) =>
      listForProject(projectId, {
        withToken,
        getParams: toURLSearchParams(params),
      }),
    {
      atom: IMPACT_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount(
    {
      fetchOnMount,
      query,
      getResponse,
    },
    id(projectId),
  )

  return { response: getResponse(), query, update }

  function getResponse(): hooks.Response<api.Impact> {
    if (projectId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([projectId])
  }

  async function query(): Promise<void> {
    if (projectId !== undefined) {
      return sharedQuery.query(projectId)
    }
  }

  async function update(payload: api.UpdateImpact): Promise<api.ApiResponse<api.Impact> | undefined> {
    if (projectId === undefined) {
      return
    }

    return sharedQuery.patch([projectId], async () => {
      return await updateProjectImpact(projectId, payload)
    })
  }
}

function id(projectId: number | string | undefined): string {
  return JSON.stringify([projectId])
}
